import React, { useMemo, useCallback, useState } from 'react';
import { useApp } from '../../../../contexts/AppContext';
import { useSession } from '@supabase/auth-helpers-react';
import { useData } from '../../../../hooks/useData';
import { useSimulation } from '../../../../hooks/useSimulation';
import { useErrorLogging, ERROR_SEVERITY, ERROR_CATEGORY } from '../../../../utils/errorLogging';
import { TABLES } from '../../../../constants';
import { MODAL_TYPES } from '../../../../constants/modalTypes';
import { supabase } from '../../../../config/supabase';
import HomeChoicesCards from './HomeChoicesCards';
import SimulationDeltaBar from './SimulationDeltaBar';
import SimulationSummaryModal from './SimulationSummaryModal';
import pageStyles from '../../../../styles/pages.module.css';
import { LoadingSpinner } from '../../../../utils/LoadingSpinner';

const HomeChoicesSection = () => {
  const { logError } = useErrorLogging();
  const { currentProject, openModal } = useApp();
  const session = useSession();

  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [isApplying, setIsApplying] = useState(false);

  // ── Data Fetching ──

  const { data: homeChoiceItems, isLoading: isLoadingChoices } = useData(
    TABLES.REFERENCE_HOME_CHOICE_ITEMS,
    {
      select: `
        *,
        master_item:master_items(
          id,
          item_name,
          item_type_id,
          item_options,
          is_bundle,
          bundle_type,
          bundle_items,
          cost_type_id,
          estimated_cost
        )
      `,
      filter: { scope: 'whole_home' },
      orFilter: `construction_type_id.is.null,construction_type_id.eq.${currentProject?.construction_type_id}`,
      enabled: Boolean(currentProject?.id),
      realtime: false,
    },
  );

  const choiceBundleChildIds = useMemo(() => {
    if (!homeChoiceItems) return [];
    const ids = [];
    homeChoiceItems.forEach(item => {
      if (item.master_item?.bundle_type === 'choice' && item.master_item?.bundle_items?.items) {
        item.master_item.bundle_items.items.forEach(child => ids.push(child.item_id));
      }
    });
    return [...new Set(ids)];
  }, [homeChoiceItems]);

  const { data: bundleChildItems } = useData(TABLES.MASTER_ITEMS, {
    select: 'id, item_name, estimated_cost',
    filter: { id: choiceBundleChildIds },
    enabled: choiceBundleChildIds.length > 0,
    realtime: false,
  });

  const {
    data: defaultOptions,
    isLoading: isLoadingDefaults,
    insert: insertDefaultOption,
    update: updateDefaultOption,
    remove: removeDefaultOption,
  } = useData(TABLES.PROJECT_DEFAULT_OPTIONS, {
    select: '*',
    filter: { project_id: currentProject?.id },
    enabled: Boolean(currentProject?.id),
    realtime: true,
  });

  const { data: projectItems } = useData(TABLES.PROJECT_ITEMS, {
    select: 'id, master_item_id, active_quote_line_id, selected_option, cost_override',
    filter: { project_id: currentProject?.id },
    enabled: Boolean(currentProject?.id),
    realtime: true,
  });

  const { data: selectedQuotes } = useData(TABLES.VENDOR_QUOTES, {
    select: 'id, master_vendor_id, master_vendor:master_vendors(name)',
    filter: { project_id: currentProject?.id, status: 'selected' },
    enabled: Boolean(currentProject?.id),
    realtime: true,
  });

  const selectedQuoteIds = useMemo(() => (selectedQuotes || []).map(q => q.id), [selectedQuotes]);
  const { data: quoteItems } = useData(TABLES.VENDOR_QUOTE_ITEMS, {
    select: 'id, quote_id, line_price, project_item_ids',
    filter: { quote_id: selectedQuoteIds },
    enabled: selectedQuoteIds.length > 0,
    realtime: true,
  });

  const { data: costTypesRaw } = useData(TABLES.REFERENCE_COST_TYPES, {
    select: 'id,name',
    realtime: false,
  });

  const costTypeMap = useMemo(() => {
    const map = {};
    if (!costTypesRaw) return map;
    costTypesRaw.forEach(ct => {
      map[ct.id] = ct.name;
    });
    return map;
  }, [costTypesRaw]);

  const { data: itemTypes } = useData(TABLES.REFERENCE_ITEM_TYPES, {
    select: 'id,name,display_order',
    realtime: false,
  });

  // ── Derived Data ──

  const vendorPriceMap = useMemo(() => {
    const map = new Map();
    if (!projectItems || !quoteItems || !selectedQuotes) return map;

    const quoteItemById = new Map(quoteItems.map(qi => [qi.id, qi]));
    const quoteById = new Map(selectedQuotes.map(q => [q.id, q]));

    projectItems.forEach(pi => {
      if (!pi.active_quote_line_id || !pi.master_item_id) return;
      const qi = quoteItemById.get(pi.active_quote_line_id);
      if (!qi || qi.line_price == null) return;
      const quote = quoteById.get(qi.quote_id);
      const vendorName = quote?.master_vendor?.name || null;

      if (!map.has(pi.master_item_id)) {
        map.set(pi.master_item_id, {
          price: parseFloat(qi.line_price),
          vendorName,
        });
      }
    });

    return map;
  }, [projectItems, quoteItems, selectedQuotes]);

  const getItemCategory = masterItem => {
    if (masterItem.is_bundle && masterItem.bundle_type === 'choice') return 'choice_bundle';
    const options = masterItem.item_options?.options;
    if (!options || !Array.isArray(options) || options.length === 0) return 'no_options';
    const groups = [...new Set(options.map(o => o.group).filter(Boolean))];
    return groups.length > 1 ? 'multi_group' : 'single_group';
  };

  const groupOptionsByGroup = rawOptions => {
    if (!rawOptions || !Array.isArray(rawOptions)) return {};
    const grouped = {};
    rawOptions.forEach(opt => {
      const group = opt.group || 'default';
      if (!grouped[group]) grouped[group] = [];
      grouped[group].push(opt);
    });
    return grouped;
  };

  const getChoiceBundleChildren = masterItem => {
    if (!masterItem.bundle_items?.items || !bundleChildItems) return [];
    return masterItem.bundle_items.items
      .map(child => {
        const mi = bundleChildItems.find(b => b.id === child.item_id);
        if (!mi) return null;
        return {
          id: mi.id,
          name: mi.item_name,
          estimated_cost: mi.estimated_cost,
          display_order: child.display_order,
        };
      })
      .filter(Boolean)
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
  };

  // Build a map of master_item_id → raw master_item (for auto-fill cost lookup)
  const masterItemMap = useMemo(() => {
    const map = {};
    if (!homeChoiceItems) return map;
    homeChoiceItems.forEach(item => {
      if (item.master_item) {
        map[item.master_item_id] = item.master_item;
      }
    });
    return map;
  }, [homeChoiceItems]);

  const tableData = useMemo(() => {
    if (!homeChoiceItems || !defaultOptions) return [];

    return homeChoiceItems
      .map(item => {
        const defaultOption = defaultOptions.find(
          opt => opt.master_item_id === item.master_item_id,
        );
        const category = getItemCategory(item.master_item);
        const options = item.master_item.item_options?.options || [];

        return {
          id: defaultOption?.id,
          master_item_id: item.master_item_id,
          item_type_id: item.master_item.item_type_id,
          item_name: item.master_item.item_name,
          cost_type_id: item.master_item.cost_type_id,
          display_order: item.display_order,
          category,
          option_groups: groupOptionsByGroup(options),
          bundle_children:
            category === 'choice_bundle' ? getChoiceBundleChildren(item.master_item) : [],
          selected_option: defaultOption?.selected_option || null,
        };
      })
      .sort((a, b) => {
        if (a.display_order !== null && b.display_order !== null) {
          return a.display_order - b.display_order;
        }
        return a.item_name.localeCompare(b.item_name);
      });
  }, [homeChoiceItems, defaultOptions, bundleChildItems]);

  // O(1) lookup maps for tableData
  const { tableDataByItemId, tableDataByMasterId } = useMemo(() => {
    const byId = {};
    const byMaster = {};
    tableData.forEach(i => {
      if (i.id) byId[i.id] = i;
      byMaster[i.master_item_id] = i;
    });
    return { tableDataByItemId: byId, tableDataByMasterId: byMaster };
  }, [tableData]);

  // Group items by item_type_id
  const groupedData = useMemo(() => {
    if (!tableData.length) return [];

    const itemTypeMap = {};
    (itemTypes || []).forEach(it => {
      itemTypeMap[it.id] = it;
    });

    const groups = {};
    tableData.forEach(item => {
      const typeId = item.item_type_id || '__other__';
      if (!groups[typeId]) {
        const typeInfo = itemTypeMap[typeId];
        groups[typeId] = {
          groupId: typeId,
          groupName: typeInfo?.name || 'אחר',
          displayOrder: typeInfo?.display_order ?? 9999,
          items: [],
          stats: { configured: 0, total: 0, costTotal: 0 },
        };
      }
      groups[typeId].items.push(item);
      groups[typeId].stats.total += 1;
      if (item.selected_option) {
        groups[typeId].stats.configured += 1;
        const cost = item.selected_option.cost || 0;
        groups[typeId].stats.costTotal += cost;
      }
    });

    return Object.values(groups).sort((a, b) => a.displayOrder - b.displayOrder);
  }, [tableData, itemTypes]);

  // Summary stats
  const summaryStats = useMemo(() => {
    let totalConfigured = 0;
    let totalItems = 0;
    let estimatedTotal = 0;

    groupedData.forEach(g => {
      totalConfigured += g.stats.configured;
      totalItems += g.stats.total;
      estimatedTotal += g.stats.costTotal;
    });

    return { totalConfigured, totalItems, estimatedTotal };
  }, [groupedData]);

  // ── Buffered editing hook (always-on) ──

  const simulation = useSimulation({
    projectId: currentProject?.id,
    tableData,
    projectItems,
    totalBudget: currentProject?.total_budget ? parseFloat(currentProject.total_budget) : null,
  });

  // Navigation protection is handled by beforeunload in useSimulation

  // ── Propagation (silent batch) ──

  const propagateToExistingItems = useCallback(
    async (masterItemId, changeType, updates) => {
      if (!projectItems || !currentProject?.id) return;

      const eligible = projectItems.filter(
        pi => pi.master_item_id === masterItemId && !pi.active_quote_line_id,
      );

      if (eligible.length === 0) return;

      if (changeType === 'choice_bundle') {
        // Choice bundle propagation not yet supported
        return;
      }

      try {
        const eligibleIds = eligible.map(pi => pi.id);

        if (changeType === 'cost') {
          const { error } = await supabase
            .from('project_items')
            .update({
              cost_override: updates.cost ?? null,
              updated_by: session?.user?.id,
              updated_at: new Date().toISOString(),
            })
            .in('id', eligibleIds);
          if (error) throw error;
        } else if (changeType === 'options') {
          const { cost, notes, ...optionSelections } = updates.selected_option || {};
          void cost;
          void notes;
          const { error } = await supabase
            .from('project_items')
            .update({
              selected_option: optionSelections,
              updated_by: session?.user?.id,
              updated_at: new Date().toISOString(),
            })
            .in('id', eligibleIds);
          if (error) throw error;
        }
      } catch (error) {
        logError(error, {
          severity: ERROR_SEVERITY.ERROR,
          category: ERROR_CATEGORY.DATA_MUTATION,
          message: 'Failed to propagate home choice changes to existing items',
          metadata: { masterItemId, changeType },
        });
      }
    },
    [projectItems, currentProject?.id, session?.user?.id, logError],
  );

  // ── DB write handler ──

  const handleUpdate = async (itemId, updates) => {
    try {
      const item = tableData.find(i => i.id === itemId || i.master_item_id === itemId);
      if (!item) {
        throw new Error(`Item not found with id: ${itemId}`);
      }

      const data = {
        project_id: currentProject.id,
        master_item_id: item.master_item_id,
        selected_option: updates.selected_option,
        updated_by: session?.user?.id,
        updated_at: new Date().toISOString(),
      };

      if (item.id) {
        await updateDefaultOption({
          id: item.id,
          data: data,
        });
      } else {
        await insertDefaultOption({
          ...data,
          created_by: session?.user?.id,
          created_at: new Date().toISOString(),
        });
      }
    } catch (error) {
      logError(error, {
        severity: ERROR_SEVERITY.ERROR,
        category: ERROR_CATEGORY.DATA_MUTATION,
        message: 'Failed to update home choice option',
      });
    }
  };

  // ── Handlers: all changes buffer through the hook ──

  const handleOptionSelect = (masterItemId, group, value) => {
    simulation.bufferOptionChange(masterItemId, group, value, masterItemMap[masterItemId]);
  };

  const handleCostChange = (itemId, cost) => {
    const item = tableDataByItemId[itemId];
    if (!item) return;
    simulation.bufferCostChange(item.master_item_id, cost);
  };

  const handleNotesChange = (itemId, notes) => {
    const item = tableDataByItemId[itemId];
    if (!item || !item.selected_option) return;

    // Notes always save immediately (not buffered)
    const selected_option = {
      ...item.selected_option,
      notes: notes || null,
    };
    handleUpdate(itemId, { selected_option });
  };

  const handleDelete = async itemId => {
    try {
      const item = tableDataByItemId[itemId];
      if (!item?.id) return;
      await removeDefaultOption(item.id);
    } catch (error) {
      logError(error, {
        severity: ERROR_SEVERITY.ERROR,
        category: ERROR_CATEGORY.DATA_MUTATION,
        message: 'Failed to delete home choice option',
      });
    }
  };

  const handleQuote = itemTypeId => {
    openModal(MODAL_TYPES.QUOTE_FORM, {
      projectId: currentProject?.id,
      preselectedItemTypes: [itemTypeId],
    });
  };

  // ── Batch save ──

  const handleApplyChanges = async () => {
    setIsApplying(true);
    try {
      for (const change of simulation.changeSummary) {
        const item = tableDataByMasterId[change.masterItemId];
        if (!item) continue;

        const newOption = { ...change.newOption };
        if (change.newCost !== change.oldCost) {
          newOption.cost = change.newCost;
        }

        // Save to project_default_options
        await handleUpdate(item.id || change.masterItemId, { selected_option: newOption });

        // Propagate cost changes silently
        if (change.oldCost !== change.newCost) {
          await propagateToExistingItems(change.masterItemId, 'cost', { cost: change.newCost });
        }

        // Propagate option changes silently
        const { cost: _c, notes: _n, ...oldOpts } = change.oldOption || {};
        const { cost: _c2, notes: _n2, ...newOpts } = change.newOption || {};
        void _c;
        void _n;
        void _c2;
        void _n2;
        if (JSON.stringify(oldOpts) !== JSON.stringify(newOpts)) {
          if (change.newOption?.selected_child_id) {
            await propagateToExistingItems(change.masterItemId, 'choice_bundle', {});
          } else {
            await propagateToExistingItems(change.masterItemId, 'options', {
              selected_option: change.newOption,
            });
          }
        }
      }

      simulation.discard();
      setShowSummaryModal(false);
    } catch (error) {
      logError(error, {
        severity: ERROR_SEVERITY.ERROR,
        category: ERROR_CATEGORY.DATA_MUTATION,
        message: 'Failed to apply changes',
      });
    } finally {
      setIsApplying(false);
    }
  };

  // ── Render ──

  if (isLoadingChoices || isLoadingDefaults) {
    return (
      <div className={pageStyles.loadingContainer}>
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <>
      <div style={{ marginBottom: '12px' }}>
        <h2 className={pageStyles.pageTitle}>אפיון חומרים</h2>
        <p className={pageStyles.pageSubtitle}>
          בחר אפשרויות עבור כל פריט. השינויים יישמרו רק כשתלחץ &quot;שמור שינויים&quot;.
        </p>
      </div>

      <HomeChoicesCards
        groupedData={groupedData}
        summaryStats={summaryStats}
        vendorPriceMap={vendorPriceMap}
        onOptionSelect={handleOptionSelect}
        onCostChange={handleCostChange}
        onNotesChange={handleNotesChange}
        onDelete={handleDelete}
        onQuote={handleQuote}
        draftChanges={simulation.draftChanges}
        deltas={simulation.deltas}
        itemCounts={simulation.itemCounts}
        costTypeMap={costTypeMap}
        changeCount={simulation.totals.changeCount}
      />

      {/* Spacer for fixed delta bar */}
      {simulation.hasChanges && <div style={{ height: '80px' }} />}

      {simulation.hasChanges && (
        <SimulationDeltaBar
          totals={simulation.totals}
          budgetPercentage={simulation.budgetPercentage}
          hasChanges={simulation.hasChanges}
          onApply={() => setShowSummaryModal(true)}
          onDiscard={simulation.discard}
        />
      )}

      {showSummaryModal && (
        <SimulationSummaryModal
          changes={simulation.changeSummary}
          totalDelta={simulation.totals.totalDelta}
          onConfirm={handleApplyChanges}
          onCancel={() => setShowSummaryModal(false)}
          isApplying={isApplying}
        />
      )}
    </>
  );
};

export default HomeChoicesSection;
